/* MolCellQuest — オンライン同期のアプリ側ブリッジ
   ──────────────────────────────────────────────────────────
   firebase-sync.js（window.MCQSync）との継ぎ目。Firebase を
   設定していなくても、モジュールが読めなくても、本体は完全に
   動き続ける。UI からは getAuthState() だけを見ればよい。
   ────────────────────────────────────────────────────────── */

import { progress, save, snapshot } from "./state.svelte.js";
import { eraForEvo } from "../../data/journeyEngine.js";

let syncState = { enabled: false, reason: "loading" };
let currentUser = null;
let leaderboard = null; // null = 読込中, [] = 空
const listeners = new Set();

function notify() {
  for (const cb of listeners) {
    try {
      cb();
    } catch (e) {
      console.error(e);
    }
  }
}

/**
 * 変更通知を受け取る。複数の画面から呼ばれる。
 *
 * ★ window.MCQBridge は「一度だけ」作る。
 *   以前はこの関数を呼ぶたびに ready:false の新しいオブジェクトで作り直して
 *   いたため、アカウント画面やランキング画面を開くと ready が false に戻り、
 *   bridgeReady() は起動時の1回しか呼ばれないので二度と true にならなかった。
 *   同期側は waitReady() で待つので、以降クラウドからの取得が止まる
 *   （＝ログインしても再読み込みするまで反映されない）。
 */
let bridgeInstalled = false;

export function installBridge(cb) {
  if (cb) listeners.add(cb);
  if (bridgeInstalled) return () => cb && listeners.delete(cb);
  bridgeInstalled = true;

  window.MCQBridge = {
    ready: false,

    /** クラウドに書き出す内容 */
    getProgress() {
      const here = eraForEvo(progress.evo);
      return {
        nickname: progress.nickname || "",
        bio: progress.bio || "",
        evo: progress.evo,
        eraId: here.era.id,
        eraName: here.era.name,
        eraEm: here.era.em,
        form: here.era.form,
        organism: snapshot().organism,
        solvedCount: Object.keys(progress.solved).length,
        full: snapshot(),
      };
    },

    /** クラウドから降ってきた進捗をマージする */
    applyRemoteProgress(remote) {
      if (!remote) return;
      mergeRemote(remote);
      save();
      notify();
    },

    onAuth(user) {
      currentUser = user;
      notify();
    },

    onSyncState(s) {
      syncState = s || { enabled: false, reason: "unknown" };
      notify();
    },

    onLeaderboard(rows) {
      leaderboard = rows || [];
      notify();
    },
  };

  return () => cb && listeners.delete(cb);
}

/** 状態の読み込みが終わったことを同期モジュールに伝える */
export function bridgeReady() {
  if (window.MCQBridge) window.MCQBridge.ready = true;
  // sync 側がまだ読み込まれていない場合に備えて、少し待って状態を確定させる
  setTimeout(() => {
    if (!window.MCQSync) {
      syncState = { enabled: false, reason: "unconfigured" };
      notify();
    }
  }, 1200);
}

export function getAuthState() {
  return {
    user: currentUser,
    syncEnabled: !!(window.MCQSync && syncState.enabled),
    reason: syncState.reason,
  };
}

export function getLeaderboard() {
  return leaderboard;
}

/* ── 認証（同期モジュールが無ければ何もしない） ─────────── */

function sync() {
  return window.MCQSync && window.MCQSync.enabled ? window.MCQSync : null;
}

export async function signUp(nickname, email, password) {
  const s = sync();
  if (!s) return { ok: false, error: "オンライン機能が設定されていません。" };
  return s.signUp(nickname, email, password);
}

export async function signIn(email, password) {
  const s = sync();
  if (!s) return { ok: false, error: "オンライン機能が設定されていません。" };
  return s.signIn(email, password);
}

export async function signOut() {
  const s = sync();
  if (s) await s.signOut();
  currentUser = null;
  leaderboard = null;
  notify();
}

/* ── マージ方針 ───────────────────────────────────────────
   集合＝和、カウンタ＝max、ベストタイム＝min、日付＝後勝ち。
   別の端末で進めた進捗と合流しても、進化ポイントは絶対に後退しない。
   ────────────────────────────────────────────────────── */

function mergeRemote(r) {
  const remote = r.full || r;
  if (!remote || typeof remote !== "object") return;

  // カウンタは大きい方
  const maxNum = (key) => {
    const v = Number(remote[key]);
    if (isFinite(v) && v > (progress[key] || 0)) progress[key] = v;
  };
  ["evo", "streak", "bestStreak", "bestCombo", "solveTimeTotal"].forEach(maxNum);

  // 集合は和
  for (const key of ["milestonesReached", "storiesSeen", "badges"]) {
    if (!Array.isArray(remote[key])) continue;
    const set = new Set([...(progress[key] || []), ...remote[key]]);
    progress[key] = [...set];
  }

  // 解答済みは和
  if (remote.solved && typeof remote.solved === "object") {
    for (const id of Object.keys(remote.solved)) {
      if (remote.solved[id]) progress.solved[id] = true;
    }
  }

  // 「あとで解き直す」の印は、id ごとに新しい操作を勝たせる。
  // 値の符号が付け外しを表すので、絶対値（＝操作時刻）が大きい方を採る。
  if (remote.review && typeof remote.review === "object") {
    if (!progress.review || typeof progress.review !== "object") progress.review = {};
    for (const [id, t] of Object.entries(remote.review)) {
      const v = Number(t);
      if (!isFinite(v) || v === 0) continue;
      const mine = Number(progress.review[id]) || 0;
      if (Math.abs(v) > Math.abs(mine)) progress.review[id] = v;
    }
  }

  /* 間違えた問題の記録。{ n, w, c } の3つとも「大きい方」を採るだけでよい。
     w（最後に誤答）と c（最後に克服）はどちらも時刻なので、
     max を採れば「あとに起きた出来事」が自然に勝ち、
     w > c という判定がそのまま端末間で一貫する。
     n は端末ごとの誤答が二重に数えられないぶん少なめに出るが、単調に増える。 */
  if (remote.missed && typeof remote.missed === "object") {
    if (!progress.missed || typeof progress.missed !== "object") progress.missed = {};
    for (const [id, e] of Object.entries(remote.missed)) {
      if (!e || typeof e !== "object") continue;
      const mine = progress.missed[id] || {};
      const pick = (k) => Math.max(Number(mine[k]) || 0, Number(e[k]) || 0);
      progress.missed[id] = { n: pick("n"), w: pick("w"), c: pick("c") };
    }
  }

  // 日別の学習量は日ごとに大きい方
  if (remote.activity && typeof remote.activity === "object") {
    for (const [day, n] of Object.entries(remote.activity)) {
      const v = Number(n) || 0;
      if (v > (progress.activity[day] || 0)) progress.activity[day] = v;
    }
  }

  // ベストタイムは小さい方
  if (remote.bestTimes && typeof remote.bestTimes === "object") {
    for (const [id, ms] of Object.entries(remote.bestTimes)) {
      const v = Number(ms);
      if (!isFinite(v)) continue;
      if (progress.bestTimes[id] == null || v < progress.bestTimes[id]) progress.bestTimes[id] = v;
    }
  }

  // 挑戦履歴はタイムスタンプで重複除去し、直近50件だけ残す
  if (remote.attempts && typeof remote.attempts === "object") {
    for (const [id, list] of Object.entries(remote.attempts)) {
      if (!Array.isArray(list)) continue;
      const mine = progress.attempts[id] || [];
      const seen = new Set(mine.map((a) => a.t));
      const merged = [...mine];
      for (const a of list) {
        if (a && !seen.has(a.t)) {
          merged.push(a);
          seen.add(a.t);
        }
      }
      merged.sort((a, b) => a.t - b.t);
      progress.attempts[id] = merged.slice(-50);
    }
  }

  // 日付文字列は後勝ち
  for (const key of ["lastActive", "missionClaimed"]) {
    if (typeof remote[key] === "string" && remote[key] > (progress[key] || "")) progress[key] = remote[key];
  }

  /* プロフィール（ニックネームと見た目）。
     起動時に organism へデフォルト値が入るので「ローカルが空なら受け入れる」
     という条件では永久に引き継がれなかった。最後に本人が変更した時刻で比べ、
     新しい方を採る。この端末で一度も設定していない（0）なら、
     アカウントに保存された姿をそのまま引き継ぐ。                       */
  const localAt = Number(progress.profileUpdatedAt) || 0;
  const remoteAt = Number(remote.profileUpdatedAt) || 0;
  if (remoteAt > localAt || localAt === 0) {
    if (remote.nickname) progress.nickname = remote.nickname;
    if (typeof remote.bio === "string") progress.bio = remote.bio;
    if (remote.organism) progress.organism = remote.organism;
    if (remoteAt > localAt) progress.profileUpdatedAt = remoteAt;
  }

  progress.eraId = eraForEvo(progress.evo).era.id;
}
